import * as React from "react";

import { cn } from "@/lib/utils";

function Card({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card"
      className={cn(
        "bg-card text-card-foreground flex flex-col gap-4 rounded-xl border pt-4 pb-[1.125rem]",
        className,
      )}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1 ps-5 pe-4 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-4",
        className,
      )}
      {...props}
    />
  );
}

/**
 * A card title is a section heading, so it renders one. It used to be a `div`,
 * which left every card outside the heading outline. A screen reader running
 * the headings of `/admin/settings` found the page title and then nothing,
 * though the page has exactly one section and that section is named. Three call
 * sites had already compensated by nesting an `<h2>` inside the div, which is
 * the same fix applied by hand at 3 of 26 places; the outline was arbitrary
 * rather than designed.
 *
 * `h2` is the default because a top-level card is a page section, the same rank
 * `DataWorkspace` gives its own title, and most cards here sit directly under
 * the page `h1`. Defaulting to `h3` skipped a level on every such page:
 * `/admin/grades` ran `h1` straight to `h3` four times. A card nested inside a
 * workspace section passes `as="h3"` so it stays under the section that holds
 * it. Tailwind preflight resets heading font-size, weight, and margin, so the
 * element change is semantic only and `[data-slot="card-title"]` keeps carrying
 * the type.
 */
type CardTitleElement = "h2" | "h3" | "h4" | "div";

function CardTitle({
  className,
  as: Component = "h2",
  ...props
}: React.ComponentProps<"div"> & { as?: CardTitleElement }) {
  return (
    <Component
      data-slot="card-title"
      className={cn("leading-none font-semibold", className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-muted-foreground text-ui", className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className,
      )}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("ps-5 pe-4", className)}
      {...props}
    />
  );
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center ps-5 pe-4 [.border-t]:pt-4", className)}
      {...props}
    />
  );
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
};
